import { ListFilters } from './list-filters';
import { VocTerm } from './voc-term';
import { DateRange } from './date-range';
import { NameElement, NameListFilters } from './name-list';
import { SifaSelection } from './sifa-selection';
import { MsContentFilter } from './ms-content-filter';
import { environment } from '../../environments/environment';

export class MssListFilters extends ListFilters {
    private _id: number | Array<number>;
    private _msType: VocTerm;
    private _country: VocTerm;
    private _city: VocTerm;
    private _library: VocTerm;
    private _copyDate: DateRange;
    private _copyist: NameElement;
    private _copyistFilters: NameListFilters;
    private _copyPlace: VocTerm;
    private _patron: NameElement;
    private _patronFilters: NameListFilters;
    private _owner: NameElement;
    private _ownerFilters: NameListFilters;
    private _sifaSelection: SifaSelection;
    private _timePeriod: DateRange;
    private _searchImages: SifaSelection;

    constructor() {
        super();
        this.init();
    }

    init(): void {
        this._msType = null;
        this._country = null;
        this._city = null;
        this._library = null;
        this._copyDate = null;
        this._copyist = null;
        this._copyistFilters = new NameListFilters();
        this._copyPlace = null;
        this._patron = null;
        this._patronFilters = new NameListFilters();
        this._owner = null;
        this._ownerFilters = new NameListFilters();
        this._sifaSelection = null;
        this._timePeriod = null;
        this._searchImages = null;
    }

    reset(): void {
        this.initAllFilters(); // super class initialization
        this.init();
    }

    /* Manuscript id filter */
    set id(value: number | Array<number>) {
        if (value) {
            this._id = value;
            this.addTermFilter('pkid', this._id);
        } else {
            this._id = null;
            this.removeTermFilter('pkid');
        }
    }

    get id(): number | Array<number> {
        return this._id;
    }

    /* Manuscript type filter */
    set msType(value: VocTerm) {
        if (value) {
            this._msType = value;
            this.addTermFilter('fkidms_types', this._msType.pkid);
        } else {
            this._msType = null;
            this.removeTermFilter('fkidms_types');
        }
    }

    get msType(): VocTerm {
        return this._msType;
    }

    /* Country filter */
    set country(value: VocTerm) {
        if (value) {
            this._country = value;
            this.addTermFilter('fkidcountries', this._country.pkid);
        } else {
            this._country = null;
            this.removeTermFilter('fkidcountries');
        }
    }

    get country(): VocTerm {
        return this._country;
    }

    /* City filter */
    set city(value: VocTerm) {
        if (value) {
            this._city = value;
            this.addTermFilter('fkidcities', this._city.pkid);
        } else {
            this._city = null;
            this.removeTermFilter('fkidcities');
        }
    }

    get city(): VocTerm {
        return this._city;
    }

    /* Library filter */
    set library(value: VocTerm) {
        if (value) {
            this._library = value;
            this.addTermFilter('fkidlibraries', this._library.pkid);
        } else {
            this._library = null;
            this.removeTermFilter('fkidlibraries');
        }
    }

    get library(): VocTerm {
        return this._library;
    }

    /* Date of Copying filter */
    set copyDate(value: DateRange) {
        const dateFields = {
            ah: {
                from: 'mss_history_copies.creation_date_from_ah',
                to: 'mss_history_copies.creation_date_to_ah'
            },
            ad: {
                from: 'mss_history_copies.creation_date_from_ad',
                to: 'mss_history_copies.creation_date_to_ad'
            }
        };

        /* Init date */
        this._copyDate = null;
        this.removeRangeFilter(dateFields.ah.from);
        this.removeRangeFilter(dateFields.ah.to);
        this.removeRangeFilter(dateFields.ad.from);
        this.removeRangeFilter(dateFields.ad.to);

        if (value && value.calendar) {
            this._copyDate = value;
            if (this._copyDate.from) {
                this.addRangeFilter(dateFields[value.calendar]['from'], 'gte', this._copyDate.from);
            }
            if (this._copyDate.to) {
                this.addRangeFilter(dateFields[value.calendar]['to'], 'lte', this._copyDate.to);
            }
        }
    }

    get copyDate(): DateRange {
        return this._copyDate;
    }

    /* Copyist filter */
    set copyist(value: NameElement) {
        if (value) {
            this._copyist = value;
            this.addMatchFilter('__copyist', this._copyist.name);
        } else {
            this._copyist = null;
            this.removeMatchFilter('__copyist');
        }
    }

    get copyist(): NameElement {
        return this._copyist;
    }

    /* Copyist name list filterings */
    set copyistFilters(value: NameListFilters) {
        if (value) {
            this._copyistFilters = value;
        } else {
            this._copyistFilters = new NameListFilters();
        }
    }

    get copyistFilters(): NameListFilters {
        return this._copyistFilters;
    }

    /* Copy place filter */
    set copyPlace(value: VocTerm) {
        if (value) {
            this._copyPlace = value;
            this.addMatchFilter('__copyPlace', this._copyPlace.pkid);
        } else {
            this._copyPlace = null;
            this.removeMatchFilter('__copyPlace');
        }
    }

    get copyPlace(): VocTerm {
        return this._copyPlace;
    }

    /* Patron filter */
    set patron(value: NameElement) {
        if (value) {
            this._patron = value;
            this.addMatchFilter('__patron', this._patron.name);
        } else {
            this._patron = null;
            this.removeMatchFilter('__patron');
        }
    }

    get patron(): NameElement {
        return this._patron;
    }

    /* Patron name list filterings */
    set patronFilters(value: NameListFilters) {
        if (value) {
            this._patronFilters = value;
        } else {
            this._patronFilters = new NameListFilters();
        }
    }

    get patronFilters(): NameListFilters {
        return this._patronFilters;
    }

    /* Owner filter */
    set owner(value: NameElement) {
        if (value) {
            this._owner = value;
            this.addMatchFilter('__owner', this._owner.name);
        } else {
            this._owner = null;
            this.removeMatchFilter('__owner');
        }
    }

    get owner(): NameElement {
        return this._owner;
    }

    /* Owner name list filterings */
    set ownerFilters(value: NameListFilters) {
        if (value) {
            this._ownerFilters = value;
        } else {
            this._ownerFilters = new NameListFilters();
        }
    }

    get ownerFilters(): NameListFilters {
        return this._ownerFilters;
    }

    /* Šifāʾ selection filter */
    set sifaSelection(value: SifaSelection) {
        if (value) {
            this._sifaSelection = value;
            if (this._sifaSelection.section) {
                this.addTermFilter('img_link_manuscripts.fkidgridstruct_sections', this._sifaSelection.section.pkid);
            } else {
                this.removeTermFilter('img_link_manuscripts.fkidgridstruct_sections');
            }
            if (this._sifaSelection.part) {
                this.addTermFilter('img_link_manuscripts.fkidgridstruct_parts', this._sifaSelection.part.pkid);
            } else {
                this.removeTermFilter('img_link_manuscripts.fkidgridstruct_parts');
            }
            if (this._sifaSelection.treatise) {
                this.addTermFilter('img_link_manuscripts.fkidgridstruct_treatises', this._sifaSelection.treatise.pkid);
            } else {
                this.removeTermFilter('img_link_manuscripts.fkidgridstruct_treatises');
            }
            if (this._sifaSelection.chapter) {
                this.addTermFilter('img_link_manuscripts.fkidgridstruct_chapters', this._sifaSelection.chapter.pkid);
            } else {
                this.removeTermFilter('img_link_manuscripts.fkidgridstruct_chapters');
            }
            if (this._sifaSelection.otherElement) {
                this.addTermFilter('img_link_manuscripts.fkidms_structure', this._sifaSelection.otherElement.pkid);
            } else {
                this.removeTermFilter('img_link_manuscripts.fkidms_structure');
            }
        } else {
            this._sifaSelection = null;
            this.removeTermFilter('img_link_manuscripts.fkidgridstruct_sections');
            this.removeTermFilter('img_link_manuscripts.fkidgridstruct_parts');
            this.removeTermFilter('img_link_manuscripts.fkidgridstruct_treatises');
            this.removeTermFilter('img_link_manuscripts.fkidgridstruct_chapters');
            this.removeTermFilter('img_link_manuscripts.fkidms_structure');
        }
    }

    get sifaSelection(): SifaSelection {
        return this._sifaSelection;
    }

    /* Time period filter (needed by Timechart) */
    set timePeriod(value: DateRange) {
        const dateFields = {
            ah: {
                from: 'mss_history_copies.creation_date_from_ah',
                to: 'mss_history_copies.creation_date_from_ah'
            },
            ad: {
                from: 'mss_history_copies.creation_date_from_ad',
                to: 'mss_history_copies.creation_date_from_ad'
            }
        };

        /* Init time period */
        this._timePeriod = null;
        this.removeRangeFilter(dateFields.ah.from);
        this.removeRangeFilter(dateFields.ah.to);
        this.removeRangeFilter(dateFields.ad.from);
        this.removeRangeFilter(dateFields.ad.to);

        if (value && value.calendar) {
            this._timePeriod = value;
            if (this._timePeriod.from) {
                this.addRangeFilter(dateFields[value.calendar]['from'], 'gte', this._timePeriod.from);
            }
            if (this._timePeriod.to) {
                this.addRangeFilter(dateFields[value.calendar]['to'], 'lte', this._timePeriod.to);
            }
        }
    }

    get timePeriod(): DateRange {
        return this._timePeriod;
    }

    /* Search manuscript images (by Šifāʾ selection) */
    set searchImages(value: SifaSelection) {
        if (value && !value.isNull()) {
            this._searchImages = value;
            const contentFilter: MsContentFilter = new MsContentFilter();

            if (this._searchImages.section) {
                contentFilter.section = this._searchImages.section.pkid;
            }
            if (this._searchImages.part) {
                contentFilter.part_from = contentFilter.part_to = this._searchImages.part.pkid;
            }
            if (this._searchImages.treatise) {
                contentFilter.treatise_from = contentFilter.treatise_to = this._searchImages.treatise.pkid;
            }
            if (this._searchImages.chapter) {
                contentFilter.chapter_from = contentFilter.chapter_to = this._searchImages.chapter.pkid;
            }
            if (this._searchImages.otherElement) {
                contentFilter.structure = this._searchImages.otherElement.pkid;
            }
            const contentFilterObj = contentFilter.getAsObject();

            if (Object.keys(contentFilterObj).length > 0) {
                this.addMatchFilter('__content', Array(contentFilterObj));
            } else {
                this.removeMatchFilter('__content');
            }
        } else {
            this._searchImages = null;
            this.removeMatchFilter('__content');
        }
    }

    get searchImages(): SifaSelection {
        return this._searchImages;
    }

}



// WEBPACK FOOTER //
// ./src/app/models/mss-list-filters.ts